import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UserService } from '../../../services/user.service';
import { MiscService } from '../../../../../services/misc.service';
import { S3UrlService } from '../../../../../shared/utilities/s3-url.service';
import { CustomToastrService } from '../../../../../services/toastr.service';
import { UserProfileResponse, UpdateUserProfileRequest, UserUpdateProfilePictureRequest, TimeZone, ViewUserProfileResponse } from '../../../models/user.model';
import { Country, State } from '../../../../clients/models/client.model';
import { UserProfileService } from '../../../services/user-profile.service';
import { TemperatureUnitService } from '../../../../dashboard/services/temperature-unit.service';
import { combinePhone, splitPhone, DEFAULT_PHONE_COUNTRY_CODE } from 'src/app/shared/utilities/phone.helper';

@Component({
  selector: 'app-userprofile',
  templateUrl: './userprofile.component.html'
})
export class UserprofileComponent implements OnInit {
  userProfileForm!: FormGroup;
  countries: Country[] = [];
  countryCodes: string[] = [];
  states: State[] = [];
  timeZones: TimeZone[] = [];
  profilePicturePreview: string = 'assets/images/profile.jpg';
  profilePictureFile: File | null = null;
  loading = false;
  selectedIOTTimezoneId = '';
  selectedIOTRoleId = '';

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<UserprofileComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { userProfile: UserProfileResponse; userId: string, iotRoleGuid: string },
    private userService: UserService,
    private miscService: MiscService,
    private s3UrlService: S3UrlService,
    private customToastrService: CustomToastrService,
    private userProfileService: UserProfileService,
    private temperatureUnitService: TemperatureUnitService
  ) { }

  ngOnInit(): void {
    this.initializeForm();
    this.loadDropdownData();
  }

  initializeForm(): void {
    this.userProfileForm = this.fb.group({
      firstName: ['', [Validators.required]],
      middleName: [''],
      lastName: ['', [Validators.required]],
      displayName: [''],
      email: [{ value: '', disabled: true }],
      phoneCountryCode: [DEFAULT_PHONE_COUNTRY_CODE],
      phone: ['', [Validators.required, Validators.maxLength(10), Validators.pattern('[0-9]*')]],
      temperatureUnit: ['Celsius', [Validators.required]],
      timezoneId: ['', [Validators.required]],
      address1: ['', [Validators.required]],
      address2: [''],
      city: ['', [Validators.required]],
      stateId: [null],
      countryId: [null, [Validators.required]],
      zipcode: ['', [Validators.required]],
      profilePicture: ['']
    });
  }

  /**
   * Builds a de-duplicated, numerically sorted list of dialing codes for the
   * phone prefix dropdown.
   */
  private buildCountryCodes(countries: Country[]): string[] {
    const codes = new Set<string>();
    countries.forEach(c => {
      if (c.countryNumberCode) {
        codes.add(c.countryNumberCode);
      }
    });
    return Array.from(codes).sort((a, b) => Number(a) - Number(b));
  }

  loadDropdownData(): void {
    let countriesLoaded = false;
    let timezonesLoaded = false;

    const checkAndPatchData = () => {
      if (countriesLoaded && timezonesLoaded) {
        this.patchFormData();
      }
    };

    // Load countries
    this.miscService.getCountries().subscribe({
      next: (countries) => {
        this.countries = countries;
        this.countryCodes = this.buildCountryCodes(countries);
        countriesLoaded = true;
        checkAndPatchData();
      },
      error: (error) => {
        console.error('Error loading countries:', error);
      }
    });

    // Load timezones
    this.miscService.getTimeZones().subscribe({
      next: (timeZones) => {
        this.timeZones = timeZones;
        timezonesLoaded = true;
        checkAndPatchData();
      },
      error: (error) => {
        console.error('Error loading timezones:', error);
      }
    });
  }

  onCountryChange(): void {
    const countryId = this.userProfileForm.get('countryId')?.value;

    if (countryId) {
      this.miscService.getStatesByCountryId(countryId).subscribe({
        next: (states) => {
          this.states = states;
        },
        error: (error) => {
          console.error('Error loading states:', error);
        }
      });
    } else {
      this.states = [];
    }
    // Reset state selection when country changes
    this.userProfileForm.patchValue({ stateId: null });
  }

  onTimezoneChange(): void {
    const timezoneId = this.userProfileForm.get('timezoneId')?.value;

    // Store IOTTimezoneId from selected timezone
    const selectedTimezone = this.timeZones.find(timezone => timezone.id === timezoneId);
    if (selectedTimezone) {
      this.selectedIOTTimezoneId = selectedTimezone.iotTimezoneId || '';
    } else {
      this.selectedIOTTimezoneId = '';
    }
  }

  patchFormData(): void {
    if (this.data.userProfile) {
      const profile = this.data.userProfile;
      const { countryCode, number } = splitPhone(profile.phone);
      this.userProfileForm.patchValue({
        firstName: profile.firstName,
        middleName: profile.middleName || '',
        lastName: profile.lastName,
        displayName: profile.displayName || '',
        email: profile.email,
        phoneCountryCode: countryCode,
        phone: number,
        temperatureUnit: profile.temperatureUnit,
        timezoneId: profile.timezoneId || '',
        iotRoleGuid: this.data.iotRoleGuid,
        address1: profile.address1 || '',
        address2: profile.address2 || '',
        city: profile.city,
        stateId: profile.stateId || null,
        countryId: profile.countryId || null,
        zipcode: profile.zipcode,
        profilePicture: profile.profilePicture || ''
      });

      // Set profile picture preview
      if (profile.profilePicture) {
        this.profilePicturePreview = this.s3UrlService.getS3Url('user', 'profilePicture', profile.profilePicture);
      }

      // Load states if countryId is available and set selected values
      if (profile.countryId) {
        this.miscService.getStatesByCountryId(profile.countryId).subscribe({
          next: (states) => {
            this.states = states;
          },
          error: (error) => {
            console.error('Error loading states:', error);
          }
        });
      }

      // Set selected timezone ID
      if (profile.timezoneId) {
        const selectedTimezone = this.timeZones.find(timezone => timezone.id === profile.timezoneId);
        if (selectedTimezone) {
          this.selectedIOTTimezoneId = selectedTimezone.iotTimezoneId || '';
        }
      }

      // Set selected IOT Role ID from data
      // Ensure we always have a value - use the passed iotRoleGuid 
      // If iotRoleGuid is not available, we'll need to use it as-is since profile updates
      // should preserve the existing role
      this.selectedIOTRoleId = this.data.iotRoleGuid || '';
    }
  }

  onFileSelect(event: any): void {
    const file = event.target.files[0];
    if (file) {
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        this.customToastrService.error('File size must be less than 5MB', 'Error');
        return;
      }

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        this.customToastrService.error('Only JPG, PNG, and GIF files are allowed', 'Error');
        return;
      }

      this.profilePictureFile = file;

      // Create preview
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.profilePicturePreview = e.target.result;
        this.userProfileForm.patchValue({ profilePicture: file.name });
      };
      reader.readAsDataURL(file);
    }
  }

  removeProfilePicture(): void {
    this.profilePictureFile = null;
    this.profilePicturePreview = 'assets/images/profile.jpg';
    this.userProfileForm.patchValue({ profilePicture: '' });
    // Reset the file input element
    const fileInput = document.getElementById('profilePictureFile') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  onSubmit(): void {
    if (this.userProfileForm.valid) {
      this.loading = true;
      const formValue = this.userProfileForm.getRawValue();

      // Ensure we always have valid values for the required IOT fields
      const finalIotRoleGuid = this.getRoleIdFallback();
      const finalIotTimezoneId = this.selectedIOTTimezoneId || this.getTimezoneIdFallback(formValue.timezoneId);

      // Ensure we have all required values before proceeding
      if (!finalIotRoleGuid) {
        console.warn('UserProfile: iotRoleGuid is empty. Profile update may fail.');
      }
      if (!finalIotTimezoneId) {
        console.warn('UserProfile: iotTimezoneId is empty. Profile update may fail.');
      }

      const updateRequest: UpdateUserProfileRequest = {
        firstName: formValue.firstName,
        middleName: formValue.middleName || '',
        lastName: formValue.lastName,
        displayName: formValue.displayName || '',
        phone: combinePhone(formValue.phoneCountryCode, formValue.phone),
        temperatureUnit: formValue.temperatureUnit,
        iotRoleGuid: finalIotRoleGuid,
        timezoneId: formValue.timezoneId || '',
        iotTimezoneId: finalIotTimezoneId,
        address1: formValue.address1 || '',
        address2: formValue.address2 || '',
        city: formValue.city,
        stateGuid: formValue.stateId || null,
        countryGuid: formValue.countryId || null,
        zipcode: formValue.zipcode,
        profilePicture: formValue.profilePicture || ''
      };

      // Update profile first
      this.userService.updateUserProfile(this.data.userId, updateRequest).subscribe({
        next: (response) => {
          this.temperatureUnitService.setUnitFromProfileValue(formValue.temperatureUnit);
          // If there's a new profile picture, upload it
          if (this.profilePictureFile) {
            const profilePictureRequest: UserUpdateProfilePictureRequest = {
              fileContent: this.profilePicturePreview?.split(',')[1].toString() || "",
              fileName: `${this.userProfileForm.value.clientId || response}_${this.userProfileForm.value.profilePicture || ""}`
            }
            this.userService.updateUserProfilePicture(this.data.userId, profilePictureRequest).subscribe({
              next: () => {
                this.refreshUserProfile();
              },
              error: (error) => {
                this.loading = false;
                console.error('Error updating profile picture:', error);
                this.customToastrService.error(error.error?.Message || 'Error updating profile picture', 'Error');
              }
            });
          } else {
            this.refreshUserProfile();
          }
        },
        error: (error) => {
          this.loading = false;
          console.error('Error updating profile:', error);
          this.customToastrService.error(error.error?.Message || 'Error updating profile', 'Error');
        }
      });
    } else {
      this.markFormGroupTouched();
    }
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.userProfileForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getFieldError(fieldName: string): string {
    const field = this.userProfileForm.get(fieldName);
    if (field?.errors) {
      if (field.errors['required']) return `${fieldName} is required`;
      if (field.errors['email']) return 'Please enter a valid email';
      if (field.errors['pattern']) return 'Please enter a valid phone number';
    }
    return '';
  }

  private markFormGroupTouched(): void {
    Object.keys(this.userProfileForm.controls).forEach(key => {
      const control = this.userProfileForm.get(key);
      control?.markAsTouched();
    });
  }

  private getTimezoneIdFallback(timezoneId: string): string {
    const selectedTimezone = this.timeZones.find(timezone => timezone.id === timezoneId);
    return selectedTimezone?.iotTimezoneId || '';
  }

  private getRoleIdFallback(): string {
    // Primary: Use the selected IOT Role ID if available
    if (this.selectedIOTRoleId) {
      return this.selectedIOTRoleId;
    }

    // Secondary: Use the role GUID from data if available
    if (this.data.iotRoleGuid) {
      return this.data.iotRoleGuid;
    }

    // Tertiary: Try to get from current user in localStorage
    try {
      const ids_loggedin_token_data = localStorage.getItem('ids_loggedin_token ');
      if (ids_loggedin_token_data) {
        const ids_loggedin_token  = JSON.parse(ids_loggedin_token_data);
        if (ids_loggedin_token .iotRoleGuid) {
          return ids_loggedin_token .iotRoleGuid;
        }
      }
    } catch (error) {
      console.error('UserProfile: Error reading current user from localStorage:', error);
    }

    // Last resort: Log warning and return empty string
    console.warn('UserProfile: Could not determine iotRoleGuid from any source.');
    return '';
  }

  /**
   * Refresh user profile after successful update
   */
  private refreshUserProfile(): void {
    const currentUserProfile = this.userProfileService.getCurrentUserProfile();
    if (currentUserProfile?.email) {
      // Fetch updated profile with timezone details
      this.userService.getUserProfileByEmail(currentUserProfile.email).subscribe({
        next: (viewProfile: ViewUserProfileResponse) => {
          // Update shared service with fresh profile data including timezone
          this.userProfileService.setUserProfile(viewProfile);
          this.loading = false;
          this.customToastrService.success('Profile updated successfully!', 'Success');
          this.dialogRef.close(true);
        },
        error: (error) => {
          this.loading = false;
          console.error('Error fetching updated profile:', error);
          // Still show success message as the update succeeded
          this.customToastrService.success('Profile updated successfully!', 'Success');
          this.dialogRef.close(true);
        }
      });
    } else {
      this.loading = false;
      this.customToastrService.success('Profile updated successfully!', 'Success');
      this.dialogRef.close(true);
    }
  }
}